import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppShell } from "../components/AppShell";
import { CompanyLogo } from "../components/CompanyLogo";
import { DivisionChips } from "../components/DivisionChips";
import { WeeklyPackButton } from "../components/WeeklyPack";
import { ExceptionDrawer, ExportButtons, RisksPanel, ScenarioPanel, TrendsPanel } from "./ExecutivePanels";
import { Card, ErrorBox, FreshnessStamp, ProvenanceBadge, STATUS_TEXT, Select, SeverityBadge, Skeleton, Sparkline, StatusDot } from "../components/ui";
import { useCreateAction, useExceptions, useKpiBoard, useKpiDefinitions, usePlants, useWhoAmI, type KpiException } from "../lib/api";
import { buildSeries, buildTiles, companiesOf, countByStatus, latestUpdate, periodsOf, plantsOf, type Tile } from "../lib/board";
import { fmtNumber, fmtPct, fmtPeriod, fmtValue, isoDatePlusDays, type Status } from "../lib/format";

const STATUS_BORDER: Record<Status, string> = {
  Red: "border-l-status-red",
  Amber: "border-l-status-amber",
  Green: "border-l-status-green",
  Grey: "border-l-brand-grey",
};

/** Executive board (CEO-01 … CEO-12, CEO-15): KPI tiles for a period and scope, ranked exception queue with root cause and comments, one-click
 * action, unresolved risks and actions, trend analysis, controlled scenario simulation and management-view exports. */
export default function ExecutiveBoard() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const board = useKpiBoard();
  const defs = useKpiDefinitions();
  const plants = usePlants();
  const exceptions = useExceptions("Open", 100);
  const whoami = useWhoAmI();
  const create = useCreateAction();

  const [period, setPeriod] = useState("");
  const [company, setCompany] = useState("");
  const [plant, setPlant] = useState("");
  const [created, setCreated] = useState<Record<string, string>>({});
  const [createError, setCreateError] = useState<string | null>(null);
  const [openException, setOpenException] = useState<string | null>(null);
  const canAct = !!whoami.data?.onesphere_roles.some((r) => ["OneSphere Executive", "OneSphere Finance", "OneSphere Admin"].includes(r));

  const snapshots = board.data?.tiles ?? [];
  const periods = useMemo(() => periodsOf(snapshots), [snapshots]);
  const effectivePeriod = period || periods[0] || "";
  const series = useMemo(() => buildSeries(snapshots), [snapshots]);
  const companies = useMemo(() => companiesOf(snapshots), [snapshots]);
  const plantCodes = useMemo(() => plantsOf(snapshots, company || undefined), [snapshots, company]);
  const tiles = useMemo(
    () => buildTiles(series, effectivePeriod, defs.data ?? [], { company: company || undefined, plant: plant || undefined }),
    [series, effectivePeriod, defs.data, company, plant],
  );
  const counts = countByStatus(tiles);
  const updated = latestUpdate(tiles);

  const kpiName = (code: string) => t(`kpi.${code}`, defs.data?.find((d) => d.name === code)?.kpi_name ?? code);
  const plantName = (code: string) => plants.data?.find((p) => p.name === code)?.plant_name ?? code;
  const unitLabel = (unit: string) => (unit ? t(`units.${unit}`, unit) : "");

  const exList = (exceptions.data?.exceptions ?? []).filter((e) => (!company || e.company === company) && (!plant || e.plant === plant));

  const onCreate = (e: KpiException) => {
    setCreateError(null);
    const scope = `${e.company}${e.plant ? ` / ${plantName(e.plant)}` : ""}`;
    create.mutate(
      {
        problem: `${kpiName(e.kpi)} — ${scope} — ${fmtPeriod(e.period, lng)}: ${fmtPct(e.deviation, lng)} ${t("exec.vsTarget")}`,
        owner_user: whoami.data?.user ?? "",
        due_date: isoDatePlusDays(7),
        priority: e.severity === "Red" ? "High" : "Medium",
        source_doctype: "KPI Exception",
        source_name: e.name,
      },
      {
        onSuccess: (r) => setCreated((c) => ({ ...c, [e.name]: r.name })),
        onError: (err) => setCreateError(err instanceof Error ? err.message : String(err)),
      },
    );
  };

  const onDrill = (tile: Tile) => {
    if (!company) setCompany(tile.series.company);
    else if (tile.series.plant && !plant) setPlant(tile.series.plant);
  };

  const filters = (
    <div className="flex flex-wrap items-end gap-3">
      <div className="self-center"><WeeklyPackButton context={{ page: "/executive", company: company || undefined, plant: plant || undefined }} /></div>
      <div className="self-center"><ExportButtons period={effectivePeriod || undefined} company={company || undefined} enabled={canAct} /></div>
      <Select label={t("exec.period")} value={effectivePeriod} onChange={setPeriod}
        options={periods.map((p) => ({ value: p, label: fmtPeriod(p, lng) }))} />
      {plantCodes.length > 0 && (
        <Select label={t("exec.site")} value={plant} onChange={setPlant}
          options={plantCodes.map((p) => ({ value: p, label: plantName(p) }))} allLabel={t("exec.allSites")} />
      )}
    </div>
  );

  return (
    <AppShell subtitle={t("tiles.executive.title")}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="mt-1 font-heading text-2xl font-semibold">{t("exec.title")}</h1>
          <p className="text-sm text-brand-grey">{t("exec.subtitle")}</p>
        </div>
        {filters}
      </div>

      {/* subsidiaries as logo chips — the division filter (CEO-02 drill-down) */}
      {companies.length > 0 && (
        <div className="mt-4">
          <DivisionChips companies={companies} value={company} onChange={(v) => { setCompany(v); setPlant(""); }} allLabel={t("exec.allDivisions")} />
        </div>
      )}

      {board.isError && <div className="mt-6"><ErrorBox error={board.error} onRetry={() => void board.refetch()} /></div>}

      {/* summary strip */}
      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
        {(["Red", "Amber", "Green"] as Status[]).map((s) => (
          <Card key={s} className="flex items-center gap-3 px-4 py-3">
            <StatusDot status={s} className="h-3 w-3" />
            <div>
              <div className={`font-heading text-2xl font-semibold ${STATUS_TEXT[s]}`}>{board.isLoading ? "…" : counts[s]}</div>
              <div className="text-[11px] uppercase tracking-wide text-brand-grey">{t(`status.${s}`)}</div>
            </div>
          </Card>
        ))}
        <Card className="px-4 py-3">
          <div className="font-heading text-2xl font-semibold text-brand-charcoal">{exceptions.isLoading ? "…" : exList.length}</div>
          <div className="text-[11px] uppercase tracking-wide text-brand-grey">{t("exec.openExceptions")}</div>
        </Card>
        <Card className="flex flex-col justify-center px-4 py-3">
          <FreshnessStamp lastUpdated={updated} />
          <div className="text-[11px] uppercase tracking-wide text-brand-grey">{t("exec.scopeLabel")}: {company || t("exec.group")}{plant ? ` · ${plantName(plant)}` : ""}</div>
        </Card>
      </div>

      {/* KPI tiles */}
      <section className="mt-6">
        {board.isLoading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-36" />)}
          </div>
        ) : tiles.length === 0 ? (
          <Card className="p-8 text-center text-sm text-brand-grey">{t("exec.noData")}</Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {tiles.map((tile) => (
              <KpiTile key={tile.series.key} tile={tile} name={kpiName(tile.series.kpi)} plantLabel={tile.series.plant ? plantName(tile.series.plant) : ""}
                unitLabel={unitLabel(tile.definition?.unit ?? "")} lng={lng} onDrill={() => onDrill(tile)} />
            ))}
          </div>
        )}
        {tiles.length > 0 && <p className="mt-2 text-[11px] text-brand-grey">{t("exec.drillHint")}</p>}
      </section>

      {/* exception queue */}
      <Card className="mt-8 overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-brand-grey-light px-4 py-3">
          <h2 className="font-heading text-base font-semibold">{t("exec.exceptions")}</h2>
          <span className="text-xs text-brand-grey">{t("exec.rankedBy")}</span>
        </div>
        {exceptions.isError ? (
          <div className="p-4"><ErrorBox error={exceptions.error} onRetry={() => void exceptions.refetch()} /></div>
        ) : exceptions.isLoading ? (
          <div className="space-y-2 p-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-8" />)}</div>
        ) : exList.length === 0 ? (
          <div className="p-6 text-center text-sm text-brand-grey">{t("exec.noExceptions")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
                <tr>
                  <th className="px-4 py-2">{t("exec.severity")}</th>
                  <th className="px-4 py-2">{t("exec.kpi")}</th>
                  <th className="px-4 py-2">{t("exec.scopeLabel")}</th>
                  <th className="px-4 py-2">{t("exec.period")}</th>
                  <th className="px-4 py-2 text-right">{t("exec.deviation")}</th>
                  <th className="px-4 py-2 text-right">{t("exec.score")}</th>
                  <th className="px-4 py-2">{t("exec.source")}</th>
                  <th className="px-4 py-2">{t("exec.action")}</th>
                </tr>
              </thead>
              <tbody>
                {exList.map((e) => (
                  <tr key={e.name} className={`border-t border-brand-grey-light hover:bg-[#F6F8FA] ${openException === e.name ? "bg-blue-50/60" : ""}`}>
                    <td className="px-4 py-2"><SeverityBadge severity={e.severity} /></td>
                    <td className="px-4 py-2 font-semibold"><button onClick={() => setOpenException(openException === e.name ? null : e.name)} className="text-left text-brand-primary hover:underline" data-exception={e.name}>{kpiName(e.kpi)}</button></td>
                    <td className="px-4 py-2 text-brand-grey"><CompanyLogo company={e.company} size={14} className="mr-1.5 align-middle" />{e.company}{e.plant ? ` · ${plantName(e.plant)}` : ""}</td>
                    <td className="px-4 py-2">{fmtPeriod(e.period, lng)}</td>
                    <td className={`px-4 py-2 text-right font-semibold ${e.severity === "Red" ? "text-status-red" : "text-status-amber"}`}>{fmtPct(e.deviation, lng)}</td>
                    <td className="px-4 py-2 text-right">{fmtNumber(e.materiality_score, lng, 0)}</td>
                    <td className="px-4 py-2"><ProvenanceBadge source={e.insight ? "ai" : "rule"} /></td>
                    <td className="px-4 py-2">
                      {created[e.name] ? (
                        <span className="text-xs font-semibold text-status-green">{t("exec.actionCreated")} {created[e.name]}</span>
                      ) : (
                        <button onClick={() => onCreate(e)} disabled={create.isPending || !whoami.data}
                          className="rounded bg-brand-primary px-3 py-1 text-xs font-semibold text-white hover:bg-brand-deep disabled:opacity-50">
                          {create.isPending ? t("exec.creating") : t("exec.createAction")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {createError && <div className="border-t border-brand-grey-light p-4"><ErrorBox error={createError} /></div>}
      </Card>
      {openException && <ExceptionDrawer name={openException} onClose={() => setOpenException(null)} plantName={plantName} />}
      <RisksPanel company={company || undefined} />
      <TrendsPanel company={company || undefined} />
      <ScenarioPanel period={effectivePeriod || undefined} company={company || undefined} enabled={canAct} />
    </AppShell>
  );
}

function KpiTile({ tile, name, plantLabel, unitLabel, lng, onDrill }: {
  tile: Tile;
  name: string;
  plantLabel: string;
  unitLabel: string;
  lng: string;
  onDrill: () => void;
}) {
  const { t } = useTranslation();
  const { point, definition, deviation, favourable, series } = tile;
  const unit = definition?.unit ?? "";
  return (
    <button onClick={onDrill} className={`text-left ${STATUS_BORDER[point.status]}`}>
      <Card className={`flex h-full flex-col gap-2 border-l-4 p-4 transition hover:-translate-y-0.5 hover:shadow-md ${STATUS_BORDER[point.status]}`}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-brand-grey">{definition?.domain ? t(`domains.${definition.domain}`, definition.domain) : ""}</div>
            <div className="font-heading text-sm font-semibold leading-snug">{name}</div>
          </div>
          <StatusDot status={point.status} />
        </div>
        <div className="flex items-end justify-between gap-2">
          <div>
            <div className="font-heading text-2xl font-semibold">{fmtValue(point.value, unit, lng, unitLabel)}</div>
            <div className="text-xs text-brand-grey">{t("exec.target")} {fmtValue(point.target, unit, lng, unitLabel)}</div>
          </div>
          <Sparkline values={series.points.map((p) => p.value)} targets={series.points.map((p) => p.target)} status={point.status} />
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className={deviation == null ? "text-brand-grey" : favourable ? "text-status-green" : "text-status-red"}>
            {deviation == null ? "–" : `${favourable ? "▲" : "▼"} ${fmtPct(deviation, lng)}`} <span className="text-brand-grey">{t("exec.vsTarget")}</span>
          </span>
          <span className="flex min-w-0 items-center gap-1 truncate text-brand-grey" title={`${series.company}${plantLabel ? ` · ${plantLabel}` : ""}`}>
            <CompanyLogo company={series.company} size={14} />
            <span className="truncate">{series.company}{plantLabel ? ` · ${plantLabel}` : ""}</span>
          </span>
        </div>
        <FreshnessStamp lastUpdated={point.last_updated} refreshClass={point.refresh_class} />
      </Card>
    </button>
  );
}
